/**
 * @fileoverview Documents issuance page - Generic document issuance with templates
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { templateService } from '@/services/templates/templateService';
// import { documentService } from '@/services/documents/documentService'; // Deprecated client-side service
import { Modal } from '@/components/common/Modal';
import { getDefaultTemplates } from '@/services/templates/defaultTemplates';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigationGuard } from '@/contexts/NavigationGuardContext';
import { useDocumentDraft } from '@/contexts/DocumentDraftContext';
import { FileText, Upload, Trash2, Loader2, Send, AlertCircle, Check, Eye, Award, Lock, ExternalLink, Save, XCircle } from 'lucide-react';
import axios from 'axios'; // Add axios for direct API calls

// --- HELPER COMPONENTS ---

const CertificateLivePreview = ({ templates, selectedTemplate, onTemplateSelect, isLoadingTemplates, fieldValues }) => {
  const { t } = useLanguage();
  const [svgContent, setSvgContent] = useState('');
  const [svgLoading, setSvgLoading] = useState(false);

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    if (templateId === 'no-template') {
      onTemplateSelect(null);
    } else {
      const template = templates.find(t => t.id === templateId);
      if (template) onTemplateSelect(template);
    }
  };

  const templateImagePath = selectedTemplate?.file_path
    ? `/uploads/${selectedTemplate.file_path.replace(/\\/g, '/')}`
    : '/templates/certificate-template.svg';

  // Fetch raw SVG content when template changes
  useEffect(() => {
    if (!selectedTemplate?.file_path) {
      setSvgContent('');
      return;
    }
    setSvgLoading(true);
    const svgUrl = `/uploads/${selectedTemplate.file_path.replace(/\\/g, '/')}`;
    fetch(svgUrl)
      .then(res => {
        const contentType = res.headers.get('content-type') || '';
        if (res.ok && contentType.includes('svg')) return res.text();
        throw new Error('Not SVG');
      })
      .then(text => setSvgContent(text))
      .catch(() => setSvgContent(''))
      .finally(() => setSvgLoading(false));
  }, [selectedTemplate?.id, selectedTemplate?.file_path]);

  // Replace {{fields}} in memory — template file is NEVER modified
  const renderedSvg = useMemo(() => {
    if (!svgContent) return '';
    return svgContent.replace(/\{\{(\w+)\}\}/g, (match, fieldName) => {
      const value = fieldValues?.[fieldName];
      if (value !== undefined && value !== null && value.toString().trim() !== '') {
        return value.toString();
      }
      return match; // keep {{field}} visible as placeholder
    });
  }, [svgContent, fieldValues]);

  return (
    <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden flex flex-col">
      <div className="bg-slate-100 border-b border-slate-200 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h3 className="font-bold flex items-center gap-2 text-slate-800">
          <Eye className="w-5 h-5 text-blue-600" />
          {t('issue.preview_title', 'Vista Previa')}
          {renderedSvg && Object.values(fieldValues || {}).some(v => v?.toString().trim()) && (
            <span className="text-[10px] font-normal text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
              <i className="fas fa-circle text-[6px] mr-1 animate-pulse"></i>
              {t('issue.live', 'En vivo')}
            </span>
          )}
        </h3>

        <div className="flex items-center gap-2">
          <label htmlFor="preview-template-select" className="text-xs font-semibold text-slate-500 whitespace-nowrap">
            {t('issue.select_template', 'Plantilla:')}
          </label>
          <select
            id="preview-template-select"
            value={selectedTemplate?.id || 'no-template'}
            onChange={handleTemplateChange}
            disabled={isLoadingTemplates}
            className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-blue-100 outline-none bg-white min-w-[140px]"
          >
            {isLoadingTemplates ? (
              <option>{t('common.loading', 'Cargando...')}</option>
            ) : (
              <>
                <option value="no-template">{t('issue.no_template', 'Predeterminada')}</option>
                {templates.length > 0 && (
                  <optgroup label={t('issue.available_templates', 'Mis Plantillas')}>
                    {templates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </optgroup>
                )}
              </>
            )}
          </select>
        </div>
      </div>
      <div className="p-6 bg-slate-50 flex items-center justify-center min-h-[300px]">
        <div className="relative w-full aspect-[1.414/1] shadow-2xl rounded-sm overflow-hidden bg-white border border-slate-200">
          {svgLoading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
          ) : renderedSvg ? (
            <div
              dangerouslySetInnerHTML={{ __html: renderedSvg }}
              className="absolute inset-0 w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg]:max-w-full [&>svg]:max-h-full"
            />
          ) : !selectedTemplate ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 bg-slate-50 p-6 text-center">
              <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mb-6 shadow-sm border border-slate-200">
                <i className="fas fa-file-alt text-4xl text-slate-300"></i>
              </div>
              <span className="text-xl font-bold text-slate-600">
                {t('issue.select_template', 'Seleccione la plantilla del certificado')}
              </span>
            </div>
          ) : (
            <img
              src={templateImagePath}
              className="absolute inset-0 w-full h-full object-contain z-0"
              alt=""
              onError={(e) => {
                e.target.src = '/templates/certificate-template.svg';
              }}
            />
          )}
        </div>
      </div>
      <div className="px-6 py-4 bg-blue-50/30 border-t border-slate-100 sm:flex items-center gap-3 hidden">
        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 text-blue-600">
          <Lock size={14} />
        </div>
        <p className="text-[10px] text-blue-700 leading-tight">
          {t('issue.preview_note', 'La plantilla seleccionada se usará como base para el certificado final, incrustando los datos de forma inalterable.')}
        </p>
      </div>
    </div>
  );
};

const BasicIssuanceForm = ({ evidenceFiles, onFileChange, onRemoveFile, onSubmit, isLoading, templates, selectedTemplate, onTemplateSelect, isLoadingTemplates, fieldValues, onFieldChange, students, loadingStudents, selectedBeneficiary, setSelectedBeneficiary }) => {
  const { t } = useLanguage();
  const [validationErrors, setValidationErrors] = useState({});

  const validateField = (fieldName, value, fieldType) => {
    if (!value || String(value).trim() === '') return null; // empty is OK, required check is separate
    switch (fieldType) {
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? null : t('validation.invalid_email', 'Email inválido');
      case 'number':
        return !isNaN(Number(value)) ? null : t('validation.invalid_number', 'Debe ser un número');
      case 'date':
        return !isNaN(Date.parse(value)) ? null : t('validation.invalid_date', 'Fecha inválida');
      default:
        return null;
    }
  };

  const handleFieldValueChange = (fieldName, value, fieldType) => {
    onFieldChange(fieldName, value);
    const error = validateField(fieldName, value, fieldType);
    setValidationErrors(prev => ({ ...prev, [fieldName]: error }));
  };

  // Template fields from the normalized table
  const templateFields = selectedTemplate?.fields || [];

  const inputClass = "w-full p-3 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all text-sm";
  const errorInputClass = "w-full p-3 bg-red-50/30 border border-red-300 rounded-lg focus:bg-white focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none transition-all text-sm";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
      <div className="lg:col-span-3 space-y-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
          {/* HEADER: 2-Column Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.2fr',
            gap: '20px',
            background: '#0575E6',
            padding: '12px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            {/* LEFT: Document Info */}
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <i className="fas fa-file-signature" style={{ color: 'white', fontSize: '14px' }}></i>
                </div>
                <h3 style={{
                  margin: 0,
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 600
                }}>
                  {t('issue.document_info', 'Información del Documento')}
                </h3>
              </div>
            </div>

            {/* RIGHT: Beneficiary Selector */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <i className="fas fa-user-graduate" style={{ color: '#fcd34d', fontSize: '18px' }}></i>
                <h4 style={{
                  margin: 0,
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: 600
                }}>
                  {t('issue.beneficiary', 'Beneficiario')}
                </h4>
              </div>

              {/* Search + Select will be added here */}
              <select
                value={selectedBeneficiary?.id || ''}
                onChange={(e) => {
                  const student = students.find(s => s.id === e.target.value);
                  setSelectedBeneficiary(student || null);
                }}
                disabled={loadingStudents}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  background: 'white',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#1e293b',
                  cursor: 'pointer'
                }}
              >
                <option value="">
                  {loadingStudents ? t('common.loading', 'Cargando...') : t('issue.select_beneficiary', 'Seleccionar beneficiario...')}
                </option>
                {students.filter(s => !s.isPending).map(student => (
                  <option key={student.id} value={student.id}>
                    {student.fullName} - {student.email}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* FORM BODY */}
          <div className="p-6">
            <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-6">
              <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-100 mb-6 flex items-start gap-3">
                <i className="fas fa-info-circle text-blue-500 mt-1"></i>
                <p className="text-sm text-blue-700">
                  {t('issue.basic_info', 'Sube un PDF ya existente para certificarlo en la blockchain. Es ideal para certificados antiguos o documentos generados externamente.')}
                </p>
              </div>
              {/* Dynamic Template Fields — all fields come from the selected template */}
              {selectedTemplate && templateFields.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {templateFields.map((field) => {
                    const fieldName = field.field_name;
                    const fieldType = field.field_type || 'text';
                    const value = fieldValues?.[fieldName] || '';
                    const error = validationErrors[fieldName];

                    return (
                      <div key={field.id || fieldName}>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                          {field.field_label}
                          {field.is_required && <span className="text-red-500 ml-0.5">*</span>}
                          <span className="ml-1.5 text-[9px] font-normal px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 uppercase">
                            {fieldType}
                          </span>
                        </label>
                        <input
                          type={fieldType === 'number' ? 'number' : fieldType === 'email' ? 'email' : fieldType === 'date' ? 'date' : 'text'}
                          value={value}
                          onChange={(e) => handleFieldValueChange(fieldName, e.target.value, fieldType)}
                          placeholder={field.placeholder || `${t('common.enter', 'Ingrese')} ${field.field_label.toLowerCase()}`}
                          required={field.is_required}
                          step={fieldType === 'number' ? 'any' : undefined}
                          className={error ? errorInputClass : inputClass}
                        />
                        {error && (
                          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" /> {error}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {!selectedTemplate && (
                <div className="bg-amber-50/50 p-4 rounded-lg border border-amber-200 flex items-start gap-3">
                  <i className="fas fa-exclamation-triangle text-amber-500 mt-0.5"></i>
                  <p className="text-sm text-amber-700">
                    {t('issue.select_template_hint', 'Seleccioná una plantilla en el panel de vista previa para ver los campos del formulario.')}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">{t('issue.evidence_files', 'Evidencias')} <span className="text-red-500">*</span></label>
                <div
                  className={`border-2 border-dashed rounded-xl p-6 transition-all ${evidenceFiles.length > 0 ? 'border-blue-500 bg-blue-50/30' : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'}`}
                >
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.zip,.rar,.dwg,.dxf,.svg,.ai,.psd,.doc,.docx,.xls,.xlsx" multiple onChange={onFileChange} id="evidence-upload" style={{ display: 'none' }} />

                  {evidenceFiles.length > 0 ? (
                    <div className="space-y-3">
                      {evidenceFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-200 hover:border-blue-400 transition-all">
                          <div className="flex items-center gap-3">
                            <i className="fas fa-file-pdf text-2xl text-blue-500"></i>
                            <div>
                              <p className="font-medium text-slate-800 text-sm">{file.name}</p>
                              <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(2)} KB</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onRemoveFile(index);
                            }}
                            className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded transition-all"
                            title={t('common.delete', 'Eliminar')}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => document.getElementById('pdf-upload')?.click()}
                        className="w-full py-2.5 text-sm text-blue-600 hover:text-blue-800 font-medium hover:bg-blue-50 rounded-lg transition-all flex items-center justify-center gap-2"
                      >
                        <i className="fas fa-plus-circle"></i>
                        {t('issue.add_more_files', 'Agregar Más Archivos')}
                      </button>
                    </div>
                  ) : (
                    <div
                      className="flex flex-col items-center cursor-pointer"
                      onClick={() => document.getElementById('evidence-upload')?.click()}
                    >
                      <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                        <i className="fas fa-cloud-upload-alt text-2xl text-slate-400"></i>
                      </div>
                      <p className="font-medium text-slate-700 mb-1">{t('issue.upload_evidence', 'Subir Evidencias')}</p>
                      <p className="text-sm text-slate-500">{t('issue.multiple_files_hint', 'Puedes seleccionar múltiples archivos')}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-6 border-t border-slate-100 mt-6">
                <button
                  type="submit"
                  disabled={isLoading || evidenceFiles.length === 0 || !selectedTemplate || templateFields.filter(f => f.is_required).some(f => !fieldValues?.[f.field_name]?.toString().trim())}
                  className="w-full py-3.5 rounded-lg bg-[#2563EB] text-white font-bold hover:bg-[#1d4ed8] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>{t('issue.processing', 'Procesando emisión...')}</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>{t('issue.submit', 'Emitir Certificado Ahora')}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="lg:col-span-2 sticky top-6">
        <CertificateLivePreview
          templates={templates}
          selectedTemplate={selectedTemplate}
          onTemplateSelect={onTemplateSelect}
          isLoadingTemplates={isLoadingTemplates}
          fieldValues={fieldValues}
        />
      </div>
    </div>
  );
};

const AdvancedIssuanceForm = ({ templates, selectedTemplate, fieldValues, freeFields, recipientEmail, recipientName, evidenceFiles, onTemplateSelect, onFieldChange, onFreeFieldsChange, onEmailChange, onNameChange, onFilesChange, onRemoveFile, onSubmit, isLoading }) => {
  const { t } = useLanguage();
  const [showAddFreeField, setShowAddFreeField] = useState(false);
  const [newFreeFieldKey, setNewFreeFieldKey] = useState('');
  const [newFreeFieldValue, setNewFreeFieldValue] = useState('');

  const handleAddFreeField = () => {
    if (newFreeFieldKey.trim() && newFreeFieldValue.trim()) {
      onFreeFieldsChange([...freeFields, { key: newFreeFieldKey.trim(), value: newFreeFieldValue.trim() }]);
      setNewFreeFieldKey('');
      setNewFreeFieldValue('');
      setShowAddFreeField(false);
    }
  };

  const handleRemoveFreeField = (index) => {
    onFreeFieldsChange(freeFields.filter((_, i) => i !== index));
  };

  const isDefaultTemplate = (template) => template.createdBy === 'system';

  const shouldSkipField = (field) => {
    const fieldName = (field.field_name || field.name).toLowerCase();
    const fieldLabel = (field.field_label || field.label).toLowerCase();
    const fieldType = field.field_type || field.type || 'text';

    return (fieldType === 'email' || fieldName.includes('email') || fieldName.includes('mail') || fieldName.includes('correo') ||
      fieldName.includes('nombre') || fieldName.includes('name') || fieldName.includes('destinatario') || fieldName.includes('recipient') ||
      fieldLabel.includes('email') || fieldLabel.includes('correo') || fieldLabel.includes('nombre') || fieldLabel.includes('name') ||
      fieldLabel.includes('destinatario') || fieldLabel.includes('recipient'));
  };

  const renderField = (field) => {
    if (shouldSkipField(field)) return null;

    // Normalize field properties
    const fieldName = field.field_name || field.name;
    const fieldLabel = field.field_label || field.label;
    const fieldType = field.field_type || field.type || 'text';
    const isRequired = field.is_required !== undefined ? field.is_required : field.required;
    const fieldOptions = field.field_options || field.options; // Assuming options might be normalized too if they existed

    const value = fieldValues[fieldName] || '';
    const labelClass = "block text-sm font-semibold text-slate-700 mb-2";
    const inputClass = "w-full p-3 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all";

    switch (fieldType) {
      case 'text': case 'email': case 'url': case 'phone': case 'number':
        return (
          <div key={field.id} className="mb-4">
            <label className={labelClass}>{fieldLabel} {isRequired && <span className="text-red-500">*</span>}</label>
            <input
              type={fieldType === 'number' ? 'number' : 'text'}
              value={value}
              onChange={(e) => onFieldChange(fieldName, fieldType === 'number' ? parseFloat(e.target.value) : e.target.value)}
              placeholder={field.placeholder || ''}
              required={isRequired}
              className={inputClass}
            />
          </div>
        );
      case 'date':
        return (
          <div key={field.id} className="mb-4">
            <label className={labelClass}>{fieldLabel} {isRequired && <span className="text-red-500">*</span>}</label>
            <input type="date" value={value} onChange={(e) => onFieldChange(fieldName, e.target.value)} required={isRequired} className={inputClass} />
          </div>
        );
      case 'select':
        return (
          <div key={field.id} className="mb-4">
            <label className={labelClass}>{fieldLabel} {isRequired && <span className="text-red-500">*</span>}</label>
            <select value={value} onChange={(e) => onFieldChange(fieldName, e.target.value)} required={isRequired} className={inputClass}>
              <option value="">{t('common.select', 'Seleccione...')}</option>
              {fieldOptions?.map((opt, idx) => <option key={idx} value={opt}>{opt}</option>)}
            </select>
          </div>
        );
      case 'textarea':
        return (
          <div key={field.id} className="mb-4">
            <label className={labelClass}>{fieldLabel} {isRequired && <span className="text-red-500">*</span>}</label>
            <textarea value={value} onChange={(e) => onFieldChange(fieldName, e.target.value)} rows={4} required={isRequired} className={`${inputClass} resize-none`} />
          </div>
        );
      case 'radio':
        return (
          <div key={field.id} className="mb-4">
            <label className={labelClass}>{fieldLabel} {isRequired && <span className="text-red-500">*</span>}</label>
            <div className="space-y-2">
              {fieldOptions?.map((opt, idx) => (
                <label key={idx} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50">
                  <input type="radio" checked={value === opt} name={fieldName} onChange={(e) => onFieldChange(fieldName, e.target.value)} value={opt} className="w-4 h-4 text-blue-600" required={isRequired} />
                  <span className="text-slate-700">{opt}</span>
                </label>
              ))}
            </div>
          </div>
        );
      case 'checkbox_group':
        return (
          <div key={field.id} className="mb-4">
            <label className={labelClass}>{fieldLabel} {isRequired && <span className="text-red-500">*</span>}</label>
            <div className="space-y-2">
              {fieldOptions?.map((opt, idx) => {
                const currentValues = Array.isArray(value) ? value : (value ? String(value).split(', ') : []);
                const isChecked = currentValues.includes(opt);
                return (
                  <label key={idx} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => {
                        let newValues = e.target.checked ? [...currentValues, opt] : currentValues.filter(v => v !== opt);
                        onFieldChange(fieldName, newValues.join(', '));
                      }}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="text-slate-700">{opt}</span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      default: return null;
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
      <div className="lg:col-span-3">
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-8">
          <div className="bg-white p-6 rounded-xl border border-slate-200 border-t-4 border-t-[#2563EB] shadow-md hover:shadow-lg transition-shadow duration-300">
            <h3 className="text-lg font-bold text-slate-800 mb-4 pb-2 border-b border-slate-100">
              <i className="fas fa-user-circle mr-2 text-blue-500"></i>
              {t('issue.step1_recipient', '1. Información del Destinatario')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">{t('issue.recipient_name', 'Nombre / Razón Social')} <span className="text-red-500">*</span></label>
                <input type="text" value={recipientName} onChange={(e) => onNameChange(e.target.value)} required className="w-full p-3 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-blue-500 outline-none" placeholder={t('issue.full_name_placeholder', 'Nombre completo')} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Email <span className="text-red-500">*</span></label>
                <input type="email" value={recipientEmail} onChange={(e) => onEmailChange(e.target.value)} required className="w-full p-3 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-blue-500 outline-none" placeholder="Email" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl border border-slate-200 border-t-4 border-t-[#2563EB] shadow-md hover:shadow-lg transition-shadow duration-300">
            <h3 className="text-lg font-bold text-slate-800 mb-4 pb-2 border-b border-slate-100">
              <i className="fas fa-file-alt mr-2 text-blue-500"></i>
              {t('issue.step2_template', '2. Plantilla (Opcional)')}
            </h3>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2">{t('issue.select_template', 'Seleccionar Plantilla')}</label>
              <p className="text-xs text-slate-500 mb-3">{t('issue.template_sync_note', 'Usa el selector en el panel lateral para elegir la plantilla visual.')}</p>
            </div>
            {selectedTemplate && (
              <div className="bg-slate-50 p-6 rounded-lg border border-slate-200 animate-fadeIn">
                <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">
                  <i className="fas fa-list-ul mr-2"></i>
                  {t('issue.template_data', 'Datos de la Plantilla')}
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  {selectedTemplate.fields.sort((a, b) => a.order - b.order).map(field => renderField(field))}
                </div>
              </div>
            )}
          </div>

          <div className="bg-white p-6 rounded-xl border border-slate-200 border-t-4 border-t-[#2563EB] shadow-md hover:shadow-lg transition-shadow duration-300">
            <h3 className="text-lg font-bold text-slate-800 mb-4 pb-2 border-b border-slate-100 flex items-center justify-between">
              <span>
                <i className="fas fa-plus-circle mr-2 text-blue-500"></i>
                {t('issue.step3_free_fields', '3. Campos Libres / Personalizados (Opcional)')}
              </span>
            </h3>
            {freeFields.length > 0 ? (
              <div className="space-y-0 mb-4 border border-slate-100 rounded-lg overflow-hidden">
                {freeFields.map((field, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors bg-white">
                    <div className="flex-1 grid grid-cols-2 gap-4">
                      <span className="font-semibold text-slate-700 text-sm flex items-center gap-2">
                        <i className="fas fa-tag text-slate-300 text-xs"></i>
                        {field.key}
                      </span>
                      <span className="text-slate-600 text-sm border-l border-slate-100 pl-4">{field.value}</span>
                    </div>
                    <button type="button" onClick={() => handleRemoveFreeField(index)} className="text-slate-400 hover:text-red-500 p-2 ml-2 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              !showAddFreeField && <p className="text-slate-500 text-sm italic mb-4">{t('issue.no_free_fields', 'No se han agregado campos adicionales.')}</p>
            )}

            {showAddFreeField ? (
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 animate-fadeIn">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-bold text-slate-700">{t('issue.new_field', 'Nuevo Campo')}</h4>
                  <button type="button" onClick={() => setShowAddFreeField(false)} className="text-xs text-slate-500 hover:text-red-500">
                    <i className="fas fa-times mr-1"></i> {t('common.cancel', 'Cancelar')}
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 items-end">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-slate-500 mb-1">{t('issue.field_name', 'Nombre del Campo')}</label>
                    <input
                      type="text"
                      value={newFreeFieldKey}
                      onChange={(e) => setNewFreeFieldKey(e.target.value)}
                      placeholder="Ej. Matrícula"
                      className="w-full p-2 bg-white border border-slate-300 rounded-lg text-sm focus:border-blue-500 outline-none"
                      autoFocus
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-slate-500 mb-1">{t('issue.field_value', 'Valor')}</label>
                    <input
                      type="text"
                      value={newFreeFieldValue}
                      onChange={(e) => setNewFreeFieldValue(e.target.value)}
                      placeholder="Ej. XYZ-123"
                      className="w-full p-2 bg-white border border-slate-300 rounded-lg text-sm focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <button
                      type="button"
                      onClick={handleAddFreeField}
                      disabled={!newFreeFieldKey.trim() || !newFreeFieldValue.trim()}
                      className="w-full py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {t('common.add', 'Agregar')}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowAddFreeField(true)}
                className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-2 px-1 py-1 rounded hover:bg-blue-50 transition-colors"
              >
                <i className="fas fa-plus-circle"></i> {t('issue.add_new_field', 'Agregar Nuevo Campo')}
              </button>
            )}
          </div>

          <div className="bg-white p-6 rounded-xl border border-slate-200 border-t-4 border-t-[#2563EB] shadow-md hover:shadow-lg transition-shadow duration-300">
            <h3 className="text-lg font-bold text-slate-800 mb-4 pb-2 border-b border-slate-100 flex items-center justify-between">
              <span>
                <i className="fas fa-paperclip mr-2 text-blue-500"></i>
                {t('issue.step4_attachments', '4. Adjuntos (Opcional)')}
              </span>
            </h3>
            <div className="space-y-4">
              <div
                className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-xl transition-all cursor-pointer ${evidenceFiles?.length > 0 ? 'border-blue-500 bg-blue-50/20' : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'}`}
                onClick={() => document.getElementById('pdf-upload-adv')?.click()}
              >
                <input type="file" id="pdf-upload-adv" accept=".pdf" multiple className="hidden" onChange={onFilesChange} />
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3 text-blue-600">
                    <Upload className="w-6 h-6" />
                  </div>
                  <p className="text-slate-600 font-medium mb-1">{t('issue.click_upload_pdf', 'Click para subir PDFs')}</p>
                  <p className="text-xs text-slate-400">{t('issue.multiple_files', 'Puedes seleccionar múltiples archivos')}</p>
                </div>
              </div>
              {evidenceFiles?.length > 0 && (
                <div className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
                  <div className="px-4 py-2 bg-slate-100 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider flex justify-between items-center">
                    <span>{t('issue.selected_files', 'Archivos Seleccionados')} ({evidenceFiles.length})</span>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto">
                    {evidenceFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 border-b border-slate-100 last:border-0 hover:bg-white transition-colors">
                        <div className="flex items-center gap-3 overflow-hidden">
                          <FileText className="w-5 h-5 text-red-500 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">{file.name}</p>
                            <p className="text-xs text-slate-400">{(file.size / 1024).toFixed(1)} KB</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => onRemoveFile(idx)}
                          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
                          title={t('issue.delete_file', 'Eliminar archivo')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="pt-6 mt-6">
            <button
              type="submit"
              disabled={isLoading || !recipientEmail || !recipientName}
              className="w-full py-4 rounded-xl bg-[#2563EB] text-white font-bold text-lg hover:bg-[#1d4ed8] transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{t('issue.issuing_doc', 'Emitiendo Documento...')}</span>
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  <span>{t('issue.issue_doc', 'Emitir Documento')}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <div className="lg:col-span-2 sticky top-6">
        <CertificateLivePreview
          templates={templates}
          selectedTemplate={selectedTemplate}
          onTemplateSelect={onTemplateSelect}
          isLoadingTemplates={false}
          fieldValues={fieldValues}
        />
      </div>
    </div>
  );
};

// --- MAIN COMPONENT ---

export const DocumentsPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  // const {connection, connect} = useWeb3(); // REMOVED: No wallet needed
  const { registerGuard, unregisterGuard } = useNavigationGuard();
  const { draft, saveDraft: saveDraftToContext, clearDraft: clearDraftFromContext } = useDocumentDraft();

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [issuedDocument, setIssuedDocument] = useState(null);

  // Form states
  const [evidenceFiles, setevidenceFiles] = useState([]); // Array of evidence files
  const [fieldValues, setFieldValues] = useState({});

  // Students/Beneficiaries state
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);

  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  // --- Detect unsaved changes ---
  const hasUnsavedChanges = useMemo(() => {
    const hasFields = Object.values(fieldValues).some(v => v?.toString().trim());
    const hasFiles = evidenceFiles.length > 0;
    return hasFields || hasFiles;
  }, [fieldValues, evidenceFiles]);

  // --- Register navigation guard so Sidebar blocks leaving ---
  useEffect(() => {
    registerGuard(() => hasUnsavedChanges);
    return () => unregisterGuard();
  }, [hasUnsavedChanges, registerGuard, unregisterGuard]);

  // --- Browser beforeunload guard (tab close / refresh) ---
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // --- Auto-save form state to in-memory context on every change ---
  useEffect(() => {
    if (hasUnsavedChanges) {
      saveDraftToContext({
        fieldValues,
        evidenceFiles: evidenceFiles.map(f => ({ name: f.name, size: f.size })), // Store metadata only
        selectedTemplateId: selectedTemplate?.id || null,
      });
    }
  }, [fieldValues, evidenceFiles, selectedTemplate, hasUnsavedChanges, saveDraftToContext]);

  // --- Restore draft from context on mount ---
  useEffect(() => {
    if (draft) {
      if (draft.fieldValues) setFieldValues(draft.fieldValues);
      // Note: evidenceFiles cannot be restored from draft (File objects can't be serialized)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  useEffect(() => {
    loadTemplates();
    loadStudents();
  }, []);

  const loadTemplates = async () => {
    setIsLoadingTemplates(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/templates', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      } else {
        // Fallback to defaults if API fails
        setTemplates(getDefaultTemplates());
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      setTemplates(getDefaultTemplates());
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const loadStudents = async () => {
    console.log('🔍 [DocumentsPage] Loading students...');
    setLoadingStudents(true);
    try {
      const token = localStorage.getItem('auth_token');
      console.log('🔍 [DocumentsPage] Token:', token ? 'exists' : 'MISSING');

      const response = await fetch('/api/users/students', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('🔍 [DocumentsPage] Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 [DocumentsPage] Data:', data);
        console.log('🔍 [DocumentsPage] Total students:', data.students?.length || 0);

        // Filter only students with wallet (not pending)
        const eligible = data.students?.filter(s => !s.isPending && s.hasWallet) || [];
        console.log('🔍 [DocumentsPage] Eligible students:', eligible.length);
        setStudents(eligible);
      } else {
        console.error('❌ [DocumentsPage] Response not OK:', response.status);
      }
    } catch (error) {
      console.error('❌ [DocumentsPage] Error:', error);
    } finally {
      setLoadingStudents(false);
      console.log('🔍 [DocumentsPage] Completed');
    }
  };

  //  Load templates and students on mount
  useEffect(() => {
    loadTemplates();
    loadStudents();
  }, []);

  // Auto-restore selected template from draft after templates load
  useEffect(() => {
    if (templates.length > 0 && draft?.selectedTemplateId && !selectedTemplate) {
      const found = templates.find(t => t.id === draft.selectedTemplateId);
      if (found) {
        setSelectedTemplate(found);
      }
    }
  }, [templates, draft, selectedTemplate]);

  const handleModeChange = (mode) => {
    setIssuanceMode(mode);
    setSelectedTemplate(null);
    setFieldValues({});
    setFreeFields([]);
    setPdfFile(null);
    setevidenceFiles([]);
    setRecipientEmail('');
    setRecipientName('');
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    const initialValues = {};
    if (template && template.fields) {
      template.fields.forEach(field => {
        // Use field_name from the normalized table
        const name = field.field_name || field.name;
        if (field.defaultValue !== undefined) {
          initialValues[name] = field.defaultValue;
        }
      });
    }
    setFieldValues(initialValues);
  };

  const handleFieldChange = (fieldName, value) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.zip', '.rar', '.dwg', '.dxf', '.svg', '.ai', '.psd', '.doc', '.docx', '.xls', '.xlsx'];

    const validFiles = files.filter(f => {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      return allowedExtensions.includes(ext);
    });

    if (validFiles.length !== files.length) {
      alert(t('common.error_invalid_file', 'Por favor, seleccione solo archivos válidos (PDF, imágenes, documentos, etc.).'));
    }

    if (validFiles.length > 0) {
      // Accumulate files instead of replacing
      setevidenceFiles(prev => [...prev, ...validFiles]);
    }

    // Reset input to allow selecting more files
    e.target.value = '';
  };

  const handleRemoveFile = (index) => {
    setevidenceFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAdvancedFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(f => f.type === 'application/pdf');

    if (validFiles.length !== files.length) {
      alert(t('issue.some_files_discarded', 'Algunos archivos no eran PDF y fueron descartados.'));
    }

    setevidenceFiles(prev => [...prev, ...validFiles]);
  };

  const handleRemoveAdvancedFile = (index) => {
    setevidenceFiles(prev => prev.filter((_, i) => i !== index));
  };

  const validateAdvancedMode = () => {
    if (!recipientEmail || !recipientEmail.includes('@')) {
      alert(t('issue.error_invalid_email', 'Por favor, ingrese un email válido.'));
      return false;
    }
    if (!recipientName || recipientName.trim().length === 0) {
      alert(t('issue.error_invalid_name', 'Por favor, ingrese el nombre o razón social.'));
      return false;
    }

    if (selectedTemplate) {
      const requiredFields = selectedTemplate.fields.filter(f => f.is_required !== undefined ? f.is_required : f.required);
      for (const field of requiredFields) {
        const fieldName = field.field_name || field.name;
        const fieldLabel = field.field_label || field.label;

        if (!fieldValues[fieldName] || String(fieldValues[fieldName]).trim() === '') {
          alert(t('issue.field_required', `El campo "${fieldLabel}" es requerido.`));
          return false;
        }
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    // Wallet validation removed - server-side signing enabled

    // Validate evidence files
    if (evidenceFiles.length === 0) {
      alert(t('issue.error_evidence_required', 'Por favor, suba al menos un archivo de evidencia.'));
      return;
    }

    // Validate beneficiary is selected
    if (!selectedBeneficiary?.id) {
      alert(t('issue.error_beneficiary_required', 'Debe seleccionar un beneficiario.'));
      return;
    }

    // Validate template is selected
    if (!selectedTemplate) {
      alert(t('issue.error_template_required', 'Por favor, seleccione una plantilla.'));
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('auth_token');

      // PASO 1: Crear certificado en la base de datos
      console.log('📝 Creating certificate...');
      const certResponse = await fetch('/api/certificates', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          templateId: selectedTemplate.id,
          fieldValues: {
            ...fieldValues,
            recipient_user_id: selectedBeneficiary.id
          }
        })
      });

      if (!certResponse.ok) {
        throw new Error('Error al crear el certificado');
      }

      const { certificateId } = await certResponse.json();
      console.log(`✅ Certificate created: ${certificateId}`);

      // PASO 2: Subir archivos de evidencia
      if (evidenceFiles.length > 0) {
        console.log(`📤 Uploading ${evidenceFiles.length} evidence files...`);
        const formData = new FormData();
        evidenceFiles.forEach(file => {
          formData.append('evidence', file);
        });

        const evidenceResponse = await fetch(`/api/certificates/${certificateId}/evidence`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (!evidenceResponse.ok) {
          throw new Error('Error al subir archivos de evidencia');
        }
        console.log('✅ Evidence files uploaded');
      }

      // PASO 3: Generar SVG del certificado
      console.log('🎨 Generating SVG...');
      const svgResponse = await fetch(`/api/certificates/${certificateId}/svg`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          templateId: selectedTemplate.id,
          fieldValues: fieldValues
        })
      });

      if (!svgResponse.ok) {
        throw new Error('Error al generar el SVG');
      }
      console.log('✅ SVG generated');

      // PASO 4: Generar paquete TAR.GZ
      console.log('📦 Generating TAR.GZ package...');
      const packageResponse = await fetch(`/api/certificates/${certificateId}/package`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!packageResponse.ok) {
        throw new Error('Error al generar el paquete TAR.GZ');
      }

      const packageData = await packageResponse.json();
      console.log('✅ TAR.GZ package generated:', packageData.packagePath);

      // PASO 5: Finalizar (Blockchain & IPFS)
      console.log('⛓️ Finalizing (Blockchain & IPFS)...');
      const finalizeResponse = await fetch(`/api/certificates/${certificateId}/finalize`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!finalizeResponse.ok) {
        throw new Error('Error al finalizar el registro en blockchain');
      }

      const finalizeData = await finalizeResponse.json();
      console.log('✅ Finalized:', finalizeData);

      // Mostrar modal de éxito
      setIssuedDocument({
        certificateId,
        packagePath: packageData.packagePath,
        hash: packageData.hash,
        ipfsHash: finalizeData.ipfsHash, // From finalize response
        txHash: finalizeData.status === 'processing_on_chain' ? 'Procesando (En Cola)' : (finalizeData.txHash || 'Pendiente'),
        documentId: certificateId,
        recipientName: fieldValues[Object.keys(fieldValues).find(k => k.toLowerCase().includes('nombre') || k.toLowerCase().includes('name'))] || 'Destinatario'
      });
      setShowSuccessModal(true);

      // Limpiar formulario
      setevidenceFiles([]);
      setFieldValues({});
      setSelectedTemplate(null);
      clearDraftFromContext();

    } catch (error) {
      console.error('❌ Error:', error);
      alert(error.message || t('common.error_generic', 'Error al procesar la solicitud.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-[#192A42] tracking-tight">{t('nav.issue', 'Emitir Certificado')}</h1>
        <p className="mt-2 text-slate-500 text-lg">{t('issue.subtitle', 'Emite certificados y documentos digitales con tecnología blockchain')}</p>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        {/* Auto-saved indicator + Discard */}
        {hasUnsavedChanges && (
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 text-xs text-green-600 bg-green-50 px-3 py-1.5 rounded-full ring-1 ring-green-200">
              <i className="fas fa-circle text-[6px] animate-pulse"></i>
              {t('issue.auto_saved', 'Datos retenidos en memoria')}
            </span>
            <button
              onClick={() => {
                if (window.confirm(t('issue.cancel_confirm', '¿Descartar todos los cambios del formulario?'))) {
                  setFieldValues({});
                  setevidenceFiles([]);
                  setSelectedTemplate(null);
                  clearDraftFromContext();
                }
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-400 hover:text-red-600 hover:bg-red-50 transition-all"
            >
              <XCircle className="w-3.5 h-3.5" />
              {t('issue.discard', 'Descartar')}
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="h-2 bg-brand-dark w-full"></div>
        <div className="p-8">
          <BasicIssuanceForm
            evidenceFiles={evidenceFiles}
            onFileChange={handleFileChange}
            onRemoveFile={handleRemoveFile}
            onSubmit={handleSubmit}
            isLoading={isSubmitting}
            templates={templates}
            selectedTemplate={selectedTemplate}
            onTemplateSelect={handleTemplateSelect}
            isLoadingTemplates={isLoadingTemplates}
            fieldValues={fieldValues}
            onFieldChange={handleFieldChange}
            students={students}
            loadingStudents={loadingStudents}
            selectedBeneficiary={selectedBeneficiary}
            setSelectedBeneficiary={setSelectedBeneficiary}
          />
        </div>
      </div>

      <Modal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)}>
        <div className="text-center p-4">
          <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl shadow-emerald-500/10">
            <Check className="w-10 h-10" />
          </div>
          <h2 className="text-2xl font-extrabold text-[#192A42]">{t('issue.success_title', '¡Documento Emitido!')}</h2>
          <p className="text-slate-500 mt-2">
            {t('issue.success_desc', 'El documento ha sido registrado en blockchain y enviado al destinatario con éxito.')}
            {issuedDocument?.issuedBatchCount && issuedDocument.issuedBatchCount > 1 && (
              <span className="block mt-2 font-semibold text-blue-600">
                {t('issue.batch_success', 'Se han emitido {{count}} documentos en total.', { count: issuedDocument.issuedBatchCount })}
              </span>
            )}
          </p>
        </div>

        <div className="space-y-4 my-6">
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
            <p className="text-xs text-slate-400 font-bold uppercase mb-1">{t('issue.recipient', 'Destinatario')}</p>
            <p className="font-bold text-[#192A42]">{issuedDocument?.recipientName}</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
            <p className="text-xs text-slate-400 font-bold uppercase mb-1">{t('issue.doc_id', 'ID Documento')}</p>
            <p className="font-mono text-sm text-blue-600">{issuedDocument?.documentId}</p>
          </div>
          {issuedDocument?.ipfsHash && (
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
              <p className="text-xs text-slate-400 font-bold uppercase mb-1">{t('issue.ipfs_cid', 'IPFS CID (Hash)')}</p>
              <p className="font-mono text-[10px] text-slate-500 truncate" title={issuedDocument.ipfsHash}>{issuedDocument.ipfsHash}</p>
            </div>
          )}
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
            <p className="text-xs text-slate-400 font-bold uppercase mb-1">{t('issue.blockchain_hash', 'TX Hash (Estado)')}</p>
            <p className="font-mono text-[10px] text-slate-500 truncate" title={issuedDocument?.txHash}>
              {issuedDocument?.txHash || t('common.pending', 'Pendiente')}
            </p>
          </div>
        </div>

        <button
          onClick={() => setShowSuccessModal(false)}
          className="w-full h-12 rounded-xl bg-[#192A42] text-white font-bold hover:bg-[#2a456b] transition-all"
        >
          {t('common.continue', 'Continuar')}
        </button>
      </Modal>
    </div>
  );
};

